import { useEffect, useRef, useState } from "react";
import { serverNow } from "@/utils/serverClock";

/**
 * Computes a countdown locally using requestAnimationFrame for zero-lag display.
 * Server provides `started_at` (ISO UTC), `paused_elapsed_ms`, `duration_ms`, `status`.
 * Returns { remainingMs, elapsedMs, secondsLeft }.
 *
 * @param {object} timer - server timer state
 * @returns {{ remainingMs: number, elapsedMs: number, secondsLeft: number }}
 */
export default function useCountdown(timer) {
  const [tick, setTick] = useState(0);
  const rafRef = useRef(null);

  const isActive = timer && (timer.status === "running" || timer.status === "paused");

  useEffect(() => {
    if (!isActive) return;
    let mounted = true;
    const loop = () => {
      if (!mounted) return;
      setTick(t => t + 1);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    // Fallback interval: Chrome/Firefox throttle requestAnimationFrame to
    // ~1 fps (or freeze it) on background tabs and unfocused windows on a
    // secondary monitor. Without this, a Big Screen on an unfocused window
    // shows a frozen countdown until the user interacts with the tab.
    // setInterval is also throttled in background (clamped to 1000 ms) but
    // never fully stops, so the display keeps ticking at least 1 fps.
    const fallbackIv = setInterval(() => {
      if (!mounted) return;
      setTick(t => t + 1);
    }, 250);
    // Also force a re-render on visibility regain so the display snaps to
    // the correct time immediately when the user brings the tab back.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && mounted) setTick(t => t + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearInterval(fallbackIv);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isActive]);

  if (!timer) return { remainingMs: 0, elapsedMs: 0, secondsLeft: 0 };

  const duration = timer.duration_ms || 30000;
  let elapsed = timer.segment_paused_elapsed_ms || 0;
  if (timer.status === "running" && timer.segment_started_at) {
    const startedMs = new Date(timer.segment_started_at).getTime();
    elapsed += serverNow() - startedMs;
  }
  elapsed = Math.max(0, Math.min(duration, elapsed));
  const remaining = Math.max(0, duration - elapsed);
  // suppress unused warning for tick state
  void tick;
  return {
    remainingMs: remaining,
    elapsedMs: elapsed,
    secondsLeft: Math.ceil(remaining / 1000),
  };
}
